import ResourceManager from "../../components/ResourceManager"
import { StringMap } from "../../types/common"
import { useI18n } from "../../hooks"
import { useOptions } from "../../options/hooks/use-options"
import { TaskViewModel } from "../api/task"
import { useTaskMutations } from "../hooks/use-task-mutations"

const TaskWorkspacesWidget = ({
  task,
  boardId,
  columnPosition,
}: {
  task: TaskViewModel
  boardId?: string
  columnPosition?: number
}) => {
  const translations = useTranslations(defaultTranslations)
  const { addWorkspaces, removeWorkspaces } = useTaskMutations()
  const { workspaceOptions } = useOptions(task.maintainerId)
  const handleAddWorkspaces = (ids: string[]) => {
    const workspaces = workspaceOptions
      .filter((workspace) => ids.includes(workspace.id))
      .map((workspace) => ({ name: workspace.name, id: workspace.id }))
    addWorkspaces(task.id, workspaces, boardId, columnPosition)
  }

  const handleRemoveWorkspace = (id: string) => {
    const workspaces = task.workspaces
      .filter((workspace) => workspace.id === id)
      .map((workspace) => ({ name: workspace.name, id: workspace.id }))
    removeWorkspaces(task.id, { workspaces, boardId, columnPosition })
  }

  const sortedWorkspaceOptions = workspaceOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedTaskWorkspaces = task.workspaces
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedWorkspaceOptions}
      resources={sortedTaskWorkspaces}
      canAddResource={task.canUpdateWorkspaces}
      canRemoveResource={task.canUpdateWorkspaces}
      onAddResources={handleAddWorkspaces}
      onRemoveResource={handleRemoveWorkspace}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults: Translations = defaultTranslations): Translations => {
  const { translations: t } = useI18n("task")
  const translations = (t?.workspacesWidget || {}) as StringMap

  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Workspaces",
  emptyOptionsMessage: "No workspaces in this organisation",
  emptyResourcesMessage: "Task is not connected to any workspace",
}

type Translations = typeof defaultTranslations

export default TaskWorkspacesWidget
